import {
  crashImg as fsdBasicCrashImg,
  glassImg,
  highwayImg,
  liabilityImg as fsdLiabilityImg,
  stolenImg,
  unexpectedImg as fsdUnexpectedImg,
  glassMobileImg,
  highwayMobileImg,
  liabilityMobileImg,
  stolenMobileImg,
  unexpectedMobileImg,
  crashMobileImg as fsdBasicCrashMobileImg,
} from '@/components/car/assets/coverages/basic/fsd';
import {
  awardImg,
  chargerImg as fsdChargerImg,
  claimsImg,
  crashImg,
  emergencyImg,
  repairImg,
  awardMobileImg,
  chargerMobileImg,
  claimsMobileImg,
  crashMobileImg,
  emergencyMobileImg,
  repairMobileImg,
} from '@/components/car/assets/coverages/overall/fsd';
import fsdHiw0 from '@/components/car/hiw/hiw-0.png';
import fsdHiw1 from '@/components/car/hiw/hiw-1.png';
import fsdHiw2 from '@/components/car/hiw/hiw-2.png';
import type { Config } from '../../car/us/en';
import { fsdUsTweets } from '../../../components/shared/CustomersReviews/defaults/tweets/fsd-us';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/fsd/og.png';

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade Autonomous Car Insurance: 50% Off Tesla FSD Miles',
  image: [ogImage.src],
  description:
    'Revolutionary Tesla FSD insurance from Lemonade: with half off FSD miles, seamless claims, full coverage options, and bundle savings. Easy and affordable from quote to claim.',
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '30',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

export const config: Config = {
  analytics: {
    page: 'fsd',
    product: 'fsd',
    dataSource: 'fsd',
    ctaId: 'gtm_button_fsd_check_prices',
    faqViewedEvent: 'fsd_faq_section.viewed',
  },
  uniclientRedirectUrl: `${process.env.NEXT_PUBLIC_UNICLIENT_URL}/you/details?flow=fsd&p=car`,
  seo: {
    title: 'Lemonade Autonomous Car Insurance: 50% Off Tesla FSD Miles',
    metaTitle: 'Lemonade Autonomous Car Insurance: 50% Off Tesla FSD Miles',
    description:
      'Revolutionary Tesla FSD insurance from Lemonade: with half off FSD miles, seamless claims, full coverage options, and bundle savings. Easy and affordable from quote to claim.',
    ogImage: ogImage.src,
    advancedRobots: true,
    jsonld: [productJsonld],
  },
  layout: {
    ...sharedConfig['en-US'].layout,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-US'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-US'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-US'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  hero: {
    title: 'Lemonade Autonomous Car\nInsurance for Tesla FSD',
    mobileTitle: 'Lemonade\nAutonomous Car\nInsurance for Tesla FSD',
    subtitle: 'Super fast everything. Seamless claims. Half the price.',
    belowCtaText: '', // intentionally empty
    mobileTitlePaddingTop: '50px',
  },
  valueProposition: {
    title: 'Driving is changing.\nInsurance should too.',
    subtitle:
      'Tesla Full Self-Driving is twice as safe, so Lemonade takes 50% off every mile driven with FSD. That means lower premiums for you and safer roads for everyone.',
    belowChartText: 'The more you drive with FSD, the less you pay. As FSD versions improve, prices go down!',
  },
  fsdHowItWorks: {
    title: 'How it works...',
    steps: [
      {
        image: fsdHiw0,
        title: 'Connect your Tesla',
        description: 'Lemonade seamlessly connects to your car',
      },
      {
        image: fsdHiw1,
        title: 'Pay a low fixed parking price',
        description: "Minimal cost when you don't drive",
      },
      {
        image: fsdHiw2,
        title: 'Pay only for miles driven',
        description: 'We charge only for miles driven each month.\nFSD miles are half the price!',
      },
    ],
  },
  drivingExperience: {
    mode: 'tesla',
    showPlayButton: false,
    fadingMask: true,
    overrideMobileAnimationStop: true,
    randomSeed: 4,
    roadGeneration: 'random',
    showMinimap: false,
    screenWrapperPosition: {
      width: '293px',
      left: '1141px',
      bottom: '165px',
      track: {
        top: '12px',
        left: '110px',
        width: '60px',
      },
    },
    screenCanvasPosition: {
      width: '274px',
      height: '180px',
      left: '1148px',
      bottom: '184px',
    },
    checkPosition: {
      bottom: '14px',
      width: '200px',
      height: '24px',
    },
    wheelPosition: {
      left: '350px',
      bottom: '85px',
    },
    syncWheelYPosition: true,
    secondaryScreenView: {
      cameraHeight: 4500,
      cameraDepthMultiplier: 0.5,
      horizonMultiplier: 0.1,
      maxYOverrideMultiplier: 10,
      canvasScale: 1.9,
      colors: {
        background: '#f7f7f7',
        road: '#ffffff',
        lane: '#ffffff',
        rumble: '#f7f7f7',
      },
    },
    sceneTransform: {
      mobile: {
        translateX: '-7px',
        scale: 1,
        translateY: '0px',
      },
      tablet: {
        translateX: '-7px',
      },
      desktop: {
        scale: 1.3,
        translateX: '-9px',
        translateY: '-95px',
      },
      extended: {
        xl1921: {
          sceneTransform: { translateX: '-7.5px', translateY: '-20px', scale: 0.55 },
        },
        xl1993: {
          sceneTransform: { translateX: '-7.5px', translateY: '-20px', scale: 0.6 },
        },
        xl2381: {
          containerHeight: '1000px',
          sceneTransform: { translateX: '-7.5px', translateY: '-80px', scale: 0.65 },
          canvasMarginTop: '120px',
        },
        xl2601: {
          sceneTransform: { translateX: '-7.5px', translateY: '-20px', scale: 0.7 },
        },
        xl2689: {
          containerHeight: '1060px',
          sceneTransform: { translateX: '-7.5px', translateY: '-250px', scale: 0.75, height: '1400px' },
        },
        xl2880: {
          containerHeight: '1120px',
          sceneTransform: { translateX: '-7.5px', translateY: '60px', scale: 0.75 },
          canvasMarginTop: '-110px',
        },
      },
    },
  },
  overallCoverages: {
    priceType: 'fixed',
    darkBackground: true,
    variant: 'modern',
    title: 'Two innovators. One breakthrough.',
    subtitle: "Tesla's cutting-edge technology meets Lemonade's insurance innovation.",
    coverages: [
      {
        background: repairMobileImg.src,
        backgroundDesktop: repairImg.src,
        title: 'Tesla repair shops and parts',
        description: "We'll get you to the nearest Tesla shop to keep repairs fast and affordable.",
        alt: 'Tesla repair shops',
      },
      {
        background: awardMobileImg.src,
        backgroundDesktop: awardImg.src,
        title: 'Award winning app',
        description: 'View your trips, see discounts, update your policy, or file claims all in the Lemonade app.',
        alt: 'Award winning app',
      },
      {
        background: crashMobileImg.src,
        backgroundDesktop: crashImg.src,
        title: 'Emergency Crash Services',
        description: "We'll detect accidents automatically and dispatch medical help.",
        alt: 'Emergency crash services',
      },
      {
        background: emergencyMobileImg.src,
        backgroundDesktop: emergencyImg.src,
        title: 'Roadside EV charging',
        description: "Stuck with a drained battery? We'll send a charging truck your way.",
        alt: 'Roadside EV charging coverage',
      },
      {
        background: claimsMobileImg.src,
        backgroundDesktop: claimsImg.src,
        title: 'World class service and claims',
        description: 'Our incredible support team is here for whatever you need.',
        alt: 'World class service',
      },
      {
        background: chargerMobileImg.src,
        backgroundDesktop: fsdChargerImg.src,
        title: 'Wall charger coverage',
        description: "If something happens to your wall charger we'll help replace it.",
        alt: 'Wall charger coverage',
      },
    ],
  },
  lowMilage: undefined,
  electricCoverages: undefined,
  basicCoverages: {
    title: 'The boring stuff stays boring',
    subtitle: "Every standard coverage you'd find in a standard car insurance policy.",
    darkBackground: false,
    variant: 'modern',
    coverages: [
      {
        background: fsdBasicCrashMobileImg.src,
        backgroundDesktop: fsdBasicCrashImg.src,
        title: "If you're in a car crash",
        description: "We'll cover your medical costs and repairs to the car.",
        alt: 'car crash insurance',
      },
      {
        background: highwayMobileImg.src,
        backgroundDesktop: highwayImg.src,
        title: "If you're stuck on the highway",
        description: 'We have roadside assistance for tows, jumps, and more.',
        alt: 'Roadside assistance for tows, jumps, and more',
      },
      {
        background: unexpectedMobileImg.src,
        backgroundDesktop: fsdUnexpectedImg.src,
        title: 'If the unexpected happens',
        description: "We'll cover damage from fires, weather, vandalism, and more.",
        alt: 'Car damage insurance',
      },
      {
        background: glassMobileImg.src,
        backgroundDesktop: glassImg.src,
        title: 'If your glass is damaged',
        description: "We'll cover repairs for windshields, moonroofs, and more.",
        alt: 'windshield and sunroof repairs coverage',
      },
      {
        background: liabilityMobileImg.src,
        backgroundDesktop: fsdLiabilityImg.src,
        title: "If you're sued for liability",
        description: "We'll help cover the costs of a lawyer to defend you.",
        alt: 'car liability insurance for lawyer expenses',
      },
      {
        background: stolenMobileImg.src,
        backgroundDesktop: stolenImg.src,
        title: 'If your car is stolen',
        description: "We'll cover the cost to replace it with a new car.",
        alt: 'stolen car insurance',
      },
    ],
  },
  showForest: false,
  bundles: {
    mode: 'tesla',
    title: 'Your home on FSD',
    subtitle: 'Why stop with FSD savings? Lemonade now offers amazing bundles on... everything.',
    ctaLabel: 'Tesla + Home Bundle',
    ctaMobileLabel: 'Tesla + Home',
    ctaUrl: `${process.env.NEXT_PUBLIC_MONOLITH_URL}/you/1?f=1&p=car,home_us_owners`,
    bundles: {
      main: undefined,
      row: [
        { desktopTitle: 'Tesla + Renters', title: 'Tesla +\nRenters', hidden: false },
        { desktopTitle: 'Tesla + Pet', title: 'Tesla +\nPet', hidden: false },
        { desktopTitle: 'Tesla + Term Life', title: 'Tesla +\nTerm Life', hidden: false },
      ],
    },
  },
  customersReviews: {
    title: "America's top-rated insurance,\nnow for your Tesla",
    subtitle:
      'Lemonade has earned 4.9 stars in the App Store, and is also top-rated by Supermoney, Clearsurance, and others.',
    darkBackground: true,
    tweets: fsdUsTweets,
    variant: 'modern',
    showFullStars: true,
  },
  enableHeaderGreenButton: false,
  faq: {
    title: 'Good to know…',
    subtitle: 'Here are a few things to know about Lemonade Car.',
    variant: 'gray',
    paddingBottom: [24, 96, 96, 96],
    data: () => [
      {
        question: 'What is the FSD discount?',
        answer:
          "Lemonade Autonomous Car insurance includes 50% off every mile driven using FSD. It's based on Tesla's safety data that FSD-driven miles are twice as safe as manual driving, so those safer miles cost you less. (And as FSD gets better expect our discounts to improve too!)",
        analytics: 'fsd_discount',
      },
      {
        question: 'How do you track my FSD miles?',
        answer:
          'With your permission, we connect directly to your Tesla through a seamless integration that shows us exactly which miles were driven in FSD versus manual driving.',
        analytics: 'fsd_tracking',
      },
      {
        question: "What's a good Tesla-friendly insurance company?",
        answer:
          "Lemonade! Our Tesla-specific innovations like Autonomous Car insurance with FSD Insurance Coverage, integration with 50% off every mile driven using Tesla's FSD is unbeatable. We connect directly to your Tesla and reward safer driving with real savings.",
        analytics: 'fsd_tesla_friendly',
      },
      {
        question: "What if I don't use FSD that much?",
        answer:
          "No problem! You'll still get competitive rates on all your regular driving miles, and any FSD miles you do drive automatically get the 50% discount. There's no minimum usage requirement.",
        analytics: 'fsd_low_usage',
      },
      {
        question: 'What if my Tesla gets damaged while on FSD miles?',
        answer:
          "Your coverage works exactly the same whether you're driving manually or using FSD. If damage occurs, we handle your claim just like any other. The FSD discount doesn't change your coverage or claims process.",
        analytics: 'fsd_damage',
      },
      {
        question: 'Can I bundle?',
        answer:
          'Absolutely! And we have (major) discounts… Lemonade will automatically bundle your car insurance for your Tesla with a renters, homeowners, pet, or term life insurance policy. You get big savings, and bundling makes it easy to manage everything in one place.',
        analytics: 'fsd_bundle',
      },
      {
        question: 'Does this work with all Tesla models?',
        answer:
          "No :( Your Tesla needs Hardware 4 (HW4) or higher for the FSD to work with this coverage. You'll also need firmware version 2025.44.25.5 or higher, which is free and easy to install directly through your car or Tesla app.",
        analytics: 'fsd_models',
      },
      {
        question: "How do you know when I'm using FSD?",
        answer:
          'Your Tesla tells us once you connect it to Lemonade. Your car handles all the data sharing seamlessly and securely.',
        analytics: 'fsd_detection',
      },
      {
        question: 'What happens if my Tesla is totaled?',
        answer:
          "If your Tesla is totaled, we'll handle it like any comprehensive claim, paying out based on your coverage limits and the car's actual cash value. The FSD discount doesn't affect your coverage levels or claim payouts.",
        analytics: 'fsd_totaled',
      },
      {
        question: 'How do claims work for Teslas?',
        answer:
          "Tesla claims work just like any other car, but we make sure you’re set up with repair partners who really get your car. Whether it's software issues, specialized parts, or finding Tesla-certified repair shops, we'll guide you through the process.",
        analytics: 'fsd_claims',
      },
      {
        question: 'How do I add a FSD discount to my existing Lemonade policy?',
        answer:
          "Existing Lemonade customers will automatically get the FSD discount when their policy renews. Once you hit renewal, we'll set up the Tesla connection (if you're not already using it) and start tracking your FSD miles for automatic discounts.",
        analytics: 'fsd_existing_policy',
      },
      {
        question: 'Will the prices get better as FSD gets safer?',
        answer:
          "Yes! As FSD continues to improve and becomes even safer, we'll adjust our pricing to reflect it. At Lemonade, we use cutting-edge technology to constantly analyze safety data and pass those improvements on to you as lower prices.",
        analytics: 'fsd_prices_improvement',
      },
    ],
  },
  footer: {
    belowCtaText: 'As low as $30/mo',
    paddingTop: [32, 64, 64, 64],
  },
  ctaGlow: true,
};

export const embedConfig = {
  ...config,
  seo: {
    ...config.seo,
    advancedRobots: true,
    noIndex: true,
  },
};
